import { computeBorrowRateBps, computeSupplyRateBps, computeAdaptiveBorrowRateBps, ADAPTIVE_TARGET_BPS } from '@/lib/poolMath'

/// The borrow rate at one utilisation, for whichever IRM the reserve is actually on: the Morpho
/// adaptive curve off its stored per-second `rateAtTarget` when `useAdaptiveIrm`, else the kinked
/// base/slope/kink model. One place so the curve and the projection can never pick different models.
function borrowRateAt(reserve, utilBps) {
  return reserve.useAdaptiveIrm
    ? computeAdaptiveBorrowRateBps(utilBps, reserve.rateAtTargetPerSec)
    : computeBorrowRateBps(utilBps, BigInt(reserve.baseRateBps), BigInt(reserve.slope1Bps), BigInt(reserve.slope2Bps), BigInt(reserve.kinkBps))
}

// Pure interest-rate-curve maths, kept out of the hook that consumes it so it can be tested
// without pulling wagmi in - the same separation `poolMath.js` already holds for the point rates.

/// The reserve's interest-rate curve, sampled across the whole 0-100% utilisation domain.
///
/// This is the function `HorizonLendingPool` itself evaluates - the same `computeBorrowRateBps` /
/// `computeSupplyRateBps` from src/lib/poolMath.js that produce the live headline rates - so the
/// curve cannot disagree with the number shown beside it. That is the reason to plot the curve
/// rather than a rate history: a history says where the rate has been, the curve says what the
/// contract will do, and only one of those answers "what happens if I borrow this much".
///
/// Sampled densely enough that the kink reads as a corner rather than a bevel, and the kink itself
/// is always included as an exact sample so the corner lands on the true utilisation rather than
/// wherever the sampling grid happened to fall.
export function buildRateCurve(reserve, samples = 101) {
  if (!reserve) return []
  const { kinkBps, reserveFactorBps } = reserve

  const points = []
  for (let i = 0; i < samples; i++) {
    points.push(Math.round((10_000 * i) / (samples - 1)))
  }
  // Pin the model's own corner as an exact sample so it lands on the true utilisation rather than
  // wherever the grid fell: the 90% target for adaptive, the configured kink for kinked.
  const corner = reserve.useAdaptiveIrm ? ADAPTIVE_TARGET_BPS : kinkBps
  if (corner > 0 && corner < 10_000) points.push(corner)
  const utils = [...new Set(points)].sort((a, b) => a - b)

  return utils.map((u) => {
    const util = BigInt(u)
    const borrow = borrowRateAt(reserve, util)
    const supply = computeSupplyRateBps(util, borrow, BigInt(reserveFactorBps))
    return {
      utilizationBps: u,
      utilizationPct: u / 100,
      borrowRatePct: Number(borrow) / 100,
      supplyRatePct: Number(supply) / 100,
    }
  })
}

/// Where utilisation lands after a supply or borrow of `deltaUnderlying` (positive supplies,
/// negative withdraws; `side` picks which half of the ratio moves).
///
/// Utilisation is borrows / (borrows + liquidity), so supplying grows the denominator and
/// borrowing moves both - which is why this exists rather than a caller nudging one number. Used
/// to place the projected marker on the curve, and to derive the projected rate the simulation
/// rows report.
export function projectUtilization(reserve, deltaUnderlying, side) {
  if (!reserve) return null
  const borrows = reserve.totalBorrows
  const liquidity = reserve.availableLiquidity
  const delta = Number(deltaUnderlying) || 0
  if (delta === 0) return reserve.utilizationBps

  let nextBorrows = borrows
  let nextLiquidity = liquidity
  if (side === 'supply') nextLiquidity = liquidity + delta
  else if (side === 'borrow') { nextBorrows = borrows + delta; nextLiquidity = liquidity - delta }

  if (nextLiquidity < 0) nextLiquidity = 0
  if (nextBorrows < 0) nextBorrows = 0
  const total = nextBorrows + nextLiquidity
  if (total <= 0) return 0
  return Math.max(0, Math.min(10_000, Math.round((nextBorrows / total) * 10_000)))
}

/// Both rates at an arbitrary utilisation, for the projected side of a `before -> after` row.
export function ratesAtUtilization(reserve, utilizationBps) {
  if (!reserve || utilizationBps == null) return { borrowRatePct: 0, supplyRatePct: 0 }
  const util = BigInt(Math.round(utilizationBps))
  const borrow = borrowRateAt(reserve, util)
  const supply = computeSupplyRateBps(util, borrow, BigInt(reserve.reserveFactorBps))
  return { borrowRatePct: Number(borrow) / 100, supplyRatePct: Number(supply) / 100 }
}
